import { useState, useRef, useCallback } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, LinearProgress,
  Snackbar, Alert, Chip, IconButton,
} from '@mui/material';
import { Upload, Close, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useDataProvider } from 'react-admin';

export interface ColumnMapping {
  csvHeader: string;
  targetField: string;
  required?: boolean;
}

export interface ParsedRow {
  [key: string]: string;
}

export interface ImportResult {
  total: number;
  success: number;
  failed: number;
  errors: { row: number; message: string }[];
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
}

function parseCSVContent(content: string): { headers: string[]; rows: ParsedRow[] } {
  const lines = content.split(/\r?\n/).filter(line => line.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = parseCSVLine(lines[0]);
  const rows = lines.slice(1).map(line => {
    const values = parseCSVLine(line);
    const row: ParsedRow = {};
    headers.forEach((header, i) => {
      row[header] = values[i] ?? '';
    });
    return row;
  });

  return { headers, rows };
}

function autoMapColumns(csvHeaders: string[], targetFields: ColumnMapping[]): ColumnMapping[] {
  return targetFields.map(target => {
    const matched = csvHeaders.find(
      h => h.toLowerCase().replace(/[\s_-]/g, '') === target.targetField.toLowerCase().replace(/[\s_-]/g, '')
    );
    return { ...target, csvHeader: matched ?? '' };
  });
}

export interface CSVImportProps {
  resourceName: string;
  targetFields: ColumnMapping[];
  onImportComplete?: (result: ImportResult) => void;
  transformRow?: (row: ParsedRow, mapping: ColumnMapping[]) => Record<string, unknown>;
}

export const CSVImport = ({
  resourceName,
  targetFields,
  onImportComplete,
  transformRow,
}: CSVImportProps) => {
  const dataProvider = useDataProvider();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const { headers, rows } = parseCSVContent(content);
      setCsvHeaders(headers);
      setParsedRows(rows);
      setColumnMapping(autoMapColumns(headers, targetFields));
      setResult(null);
      setOpen(true);
    };
    reader.readAsText(file);
    event.target.value = '';
  }, [targetFields]);

  const handleMappingChange = (index: number, csvHeader: string) => {
    setColumnMapping(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], csvHeader };
      return updated;
    });
  };

  const handleImport = async () => {
    setImporting(true);
    let success = 0;
    let failed = 0;
    const errors: { row: number; message: string }[] = [];

    const requiredFields = columnMapping.filter(m => m.required && m.csvHeader);

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];

      const missingRequired = requiredFields.filter(m => !row[m.csvHeader]?.trim());
      if (missingRequired.length > 0) {
        failed++;
        errors.push({ row: i + 2, message: `Missing required: ${missingRequired.map(m => m.targetField).join(', ')}` });
        continue;
      }

      try {
        const data: Record<string, unknown> = {};
        columnMapping.forEach(m => {
          if (m.csvHeader) {
            data[m.targetField] = row[m.csvHeader];
          }
        });

        if (transformRow) {
          Object.assign(data, transformRow(row, columnMapping));
        }

        await dataProvider.create(resourceName, { data });
        success++;
      } catch (err) {
        failed++;
        errors.push({ row: i + 2, message: err instanceof Error ? err.message : 'Import failed' });
      }
    }

    const importResult = { total: parsedRows.length, success, failed, errors };
    setResult(importResult);
    setImporting(false);
    onImportComplete?.(importResult);

    if (failed === 0) {
      setSnackbar({ open: true, message: `Successfully imported ${success} records`, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: `Imported ${success}, failed ${failed}`, severity: 'error' });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setParsedRows([]);
    setCsvHeaders([]);
    setColumnMapping([]);
    setResult(null);
  };

  const previewRows = parsedRows.slice(0, 10);
  const mappedFields = columnMapping.filter(m => m.csvHeader);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<Upload />}
        onClick={() => fileInputRef.current?.click()}
        sx={{ textTransform: 'none' }}
      >
        Import CSV
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Import {resourceName}
          <IconButton onClick={handleClose}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {importing && <LinearProgress sx={{ mb: 2 }} />}

          {!result && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Column Mapping</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {columnMapping.map((mapping, idx) => (
                  <Box key={mapping.targetField} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 500 }}>
                      {mapping.targetField}
                      {mapping.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1, height: 18 }} />}
                    </Typography>
                    <TextField
                      size="small"
                      select
                      value={mapping.csvHeader}
                      onChange={(e) => handleMappingChange(idx, e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 200 }}
                    >
                      <option value="">-- Skip --</option>
                      {csvHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </TextField>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {result && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Import Results</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip icon={<CheckCircle />} label={`${result.success} imported`} color="success" />
                {result.failed > 0 && <Chip icon={<ErrorIcon />} label={`${result.failed} failed`} color="error" />}
              </Box>
              {result.errors.length > 0 && (
                <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                  {result.errors.map((err, i) => (
                    <Typography key={i} variant="caption" display="block" color="error">
                      Row {err.row}: {err.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Preview ({parsedRows.length} rows total, showing first {previewRows.length})
          </Typography>
          <Box sx={{ overflow: 'auto', maxHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {mappedFields.map(m => (
                    <TableCell key={m.targetField} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {m.targetField}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {mappedFields.map(m => (
                      <TableCell key={m.targetField}>{row[m.csvHeader] || ''}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {!result ? (
            <Button
              variant="contained"
              disabled={importing || mappedFields.length === 0}
              onClick={handleImport}
            >
              {importing ? 'Importing...' : `Import ${parsedRows.length} Records`}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleClose}>Done</Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export { parseCSVContent };
